import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import RelocationForm from "./RelocationForm";
import { useDispatch } from "react-redux";
import { fetchRelocationForm, updateRelocationForm } from "../../../../store/slices/relocationFormsSlice";
import { camelCase, snakeCase } from "lodash";

const EditRelocation = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        caseId: caseId || null,
        patientId: patientId || null,
        standardName: "", issueDate: "", reviewDate: "", documentNumber: "", revisionNumber: "", departmentName: "",
        patientName: "", patientGender: "", patientDob: "", patientContact: "", currentMedication: "",
        quetiapine: "", quetiapineDose: "", quetiapineType: "",
        serenace: "", serenaceDose: "", serenaceType: "",
        avil: "", avilDose: "", avilType: "",
        clopixol: "", clopixolDose: "", clopixolType: "",
        dormicum: "", dormicumDose: "", dormicumType: "",
        vallium: "", valliumDose: "", valliumType: "",
        procyclidine: "", procyclidineDose: "",
        doctorRemarks: "", doctorName: "", doctorDesignation: "", doctorPlace: "",
        rnQuetiapine: "", rnQuetiapineDose: "",
        rnSerenace: "", rnSerenaceDose: "", rnSerenaceType: "",
        rnAvil: "", rnAvilDose: "", rnAvilType: "",
        rnClopixol: "", rnClopixolDose: "", rnClopixolType: "",
        rnDormicum: "", rnDormicumDose: "", rnDormicumType: "",
        rnVallium: "", rnValliumDose: "", rnValliumType: "",
        rnProcyclidine: "", rnProcyclidineDose: "",
        rnRemarks: "", rnName: "", rnDesignation: "", rnPlace: ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Convert snake_case object to camelCase
    const toCamelCase = (obj) => {
        const result = {};
        for (let key in obj) {
            result[camelCase(key)] = obj[key];
        }
        return result;
    };

    // Convert camelCase object to snake_case for backend
    const toSnakeCase = (obj) => {
        const result = {};
        for (let key in obj) {
            result[snakeCase(key)] = obj[key];
        }
        return result;
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await dispatch(fetchRelocationForm(id)).unwrap();
                const camelData = toCamelCase(response);
                setFormData(prev => ({ ...prev, ...camelData }));
            } catch (err) {
                Swal.fire("Error", "Failed to fetch relocation form data.", "error");
            }
        };

        if (id) loadData();
    }, [id, dispatch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Create a copy of formData to modify
            const formDataToSend = { ...formData };

            // List of medication fields and their corresponding dose/type fields
            const medications = [
                { parent: 'quetiapine', dose: 'quetiapineDose', type: 'quetiapineType' },
                { parent: 'serenace', dose: 'serenaceDose', type: 'serenaceType' },
                { parent: 'avil', dose: 'avilDose', type: 'avilType' },
                { parent: 'clopixol', dose: 'clopixolDose', type: 'clopixolType' },
                { parent: 'dormicum', dose: 'dormicumDose', type: 'dormicumType' },
                { parent: 'vallium', dose: 'valliumDose', type: 'valliumType' },
                { parent: 'procyclidine', dose: 'procyclidineDose', type: 'procyclidineType' },
                { parent: 'rnQuetiapine', dose: 'rnQuetiapineDose', type: 'rnQuetiapineType' },
                { parent: 'rnSerenace', dose: 'rnSerenaceDose', type: 'rnSerenaceType' },
                { parent: 'rnAvil', dose: 'rnAvilDose', type: 'rnAvilType' },
                { parent: 'rnClopixol', dose: 'rnClopixolDose', type: 'rnClopixolType' },
                { parent: 'rnDormicum', dose: 'rnDormicumDose', type: 'rnDormicumType' },
                { parent: 'rnVallium', dose: 'rnValliumDose', type: 'rnValliumType' },
                { parent: 'rnProcyclidine', dose: 'rnProcyclidineDose', type: 'rnProcyclidineType' },
            ];

            // Process each medication
            medications.forEach(med => {
                // If parent is not "Yes", set dose and type to null
                if (formDataToSend[med.parent] !== "Yes") {
                    formDataToSend[med.dose] = null;
                    formDataToSend[med.type] = null;
                }
            });

            // Convert to snake_case for backend
            const payload = toSnakeCase(formDataToSend);

            await dispatch(updateRelocationForm({ case_id: caseId, relocationData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Relocation form updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            Swal.fire({
                title: "Error!",
                text: err?.message || "Failed to update relocation form.",
                icon: "error"
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/relocation-form/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <RelocationForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-3 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditRelocation;
